import DeleteIcon from '@mui/icons-material/Delete';
import { randomUUID } from '@/utils/uuid';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { ResultadoBinario } from '@/types/enums';

import { HpgDialogPoolCard } from './HpgDialogPoolCard';
import type { PoolLocal, PosicionHpgValida, SedFhComplementarioState } from './hpgDialogTypes';

const MAX_TOTAL = 25;
const MAX_POOLS = 10;

interface HpgDialogSedFhTabProps {
  state: SedFhComplementarioState;
  posicionesHpgValidas: PosicionHpgValida[];
  onChange: (patch: Partial<SedFhComplementarioState>) => void;
  onEliminarTab: () => void;
  showErrors?: boolean;
  disabled?: boolean;
}

export function HpgDialogSedFhTab({
  state,
  posicionesHpgValidas,
  onChange,
  onEliminarTab,
  showErrors = false,
  disabled = false,
}: HpgDialogSedFhTabProps): JSX.Element {
  const pools = state.pools as PoolLocal[];
  const totalMuestras = pools.reduce((s, p) => s + p.numeros_muestras.length, 0);
  const positivos = pools.filter((p) => p.valor === ResultadoBinario.POSITIVO).length;
  const atMaxPools = pools.length >= MAX_POOLS;
  const totalExcedido = totalMuestras > MAX_TOTAL;

  function handlePoolChange(index: number, updated: Partial<PoolLocal>): void {
    onChange({ pools: pools.map((p, i) => (i === index ? { ...p, ...updated } : p)) });
  }

  function handlePoolDelete(index: number): void {
    onChange({ pools: pools.filter((_, i) => i !== index) });
  }

  function handleAddPool(): void {
    onChange({
      pools: [
        ...pools,
        { id: randomUUID(), numeros_muestras: [], valor: null, observacion: null },
      ],
    });
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Tooltip title="Eliminar este Sed. Fh">
          <IconButton size="small" color="error" onClick={onEliminarTab}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
        >
          <Box>
            <Typography variant="subtitle1">Pools</Typography>
            <Typography variant="body2" color={totalExcedido ? 'error' : 'text.secondary'}>
              {pools.length} pools · {totalMuestras} muestras totales (máx {MAX_TOTAL})
            </Typography>
          </Box>
          <Chip
            label={`${positivos} pos`}
            size="small"
            color={positivos > 0 ? 'success' : 'default'}
          />
        </Box>

        <Stack spacing={2}>
          {pools.map((pool, index) => {
            const allUsedInOtherPools = new Set(
              pools.flatMap((p, i) => (i === index ? [] : p.numeros_muestras)),
            );
            return (
              <HpgDialogPoolCard
                key={pool.id}
                pool={pool}
                poolIndex={index}
                posicionesHpgValidas={posicionesHpgValidas}
                allUsedInOtherPools={allUsedInOtherPools}
                onChange={(updated) => handlePoolChange(index, updated)}
                onDelete={() => handlePoolDelete(index)}
                canDelete={pools.length > 1}
                showErrors={showErrors}
                disabled={disabled}
              />
            );
          })}
        </Stack>

        <Tooltip title={atMaxPools ? 'Máximo 10 pools alcanzado.' : ''} placement="top">
          <span>
            <Button
              variant="outlined"
              fullWidth
              disabled={atMaxPools || disabled}
              onClick={handleAddPool}
              sx={{ mt: 2 }}
            >
              + Agregar pool
            </Button>
          </span>
        </Tooltip>
      </Paper>

    </Box>
  );
}
